import { cur } from '@/lib/currency';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calculator, Building2, Banknote, AlertTriangle, CheckCircle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export function RecordPaymentDialog({
  open, onOpenChange, platforms, branches = [], paymentForm, setPaymentForm,
  calculatedPayment, setCalculatedPayment, calculating,
  onCalculate, onRecalcFromTotalSales, onSubmit, isEditing
}) {
  const [bankMatching, setBankMatching] = useState(false);
  const [bankMatches, setBankMatches] = useState(null);

  const selectedPlatform = platforms.find(p => p.id === paymentForm.platform_id);
  const agreedRate = parseFloat(selectedPlatform?.commission_rate || 0);
  const totalSales = parseFloat(paymentForm.total_sales) || 0;
  const amountReceived = parseFloat(paymentForm.amount_received) || 0;
  const expectedCommission = Math.round(totalSales * (agreedRate / 100) * 100) / 100;
  const expectedReceived = Math.round((totalSales - expectedCommission) * 100) / 100;
  const actualCommission = totalSales > 0 ? totalSales - amountReceived : 0;
  const actualRate = totalSales > 0 ? Math.round((actualCommission / totalSales) * 10000) / 100 : 0;
  const rateVariance = Math.round((actualRate - agreedRate) * 100) / 100;
  const amountVariance = Math.round((expectedReceived - amountReceived) * 100) / 100;
  const hasVariance = totalSales > 0 && amountReceived > 0 && Math.abs(amountVariance) > 0.5;

  // Inter-branch dues preview — when the deposit landed in branch R but
  // other branches earned a share, show what R will owe each of them.
  const receivedBranchId = paymentForm.received_branch_id || '';
  const interBranchDues = (calculatedPayment?.branch_breakdown || [])
    .filter(b => receivedBranchId && b.branch_id !== receivedBranchId && (b.expected_amount || b.amount_received || 0) > 0)
    .map(b => ({
      branch_id: b.branch_id,
      branch_name: b.branch_name,
      amount: b.amount_received != null ? b.amount_received : b.expected_amount,
    }));
  const totalDuePreview = interBranchDues.reduce((s, r) => s + (r.amount || 0), 0);

  const loadFromBank = async () => {
    if (!paymentForm.platform_id || !paymentForm.period_start || !paymentForm.period_end) {
      toast.error('Select platform and date range first'); return;
    }
    setBankMatching(true);
    try {
      const res = await api.get(`/platform-payments/bank-match?platform_id=${paymentForm.platform_id}&period_start=${paymentForm.period_start}&period_end=${paymentForm.period_end}`);
      setBankMatches(res.data);
      if (res.data.total_matched > 0) {
        // Auto-fill the bank reference/transaction id into reference_number
        // (only when the user hasn't typed one already).
        const refs = (res.data.matches || []).map((m) => m.reference).filter(Boolean);
        const autoRef = refs.length
          ? [...new Set(refs)].join(', ').slice(0, 120)
          : `${res.data.matches?.[0]?.bank || 'BANK'} ${res.data.matches?.[0]?.date || ''}`.trim();
        setPaymentForm(prev => ({
          ...prev,
          amount_received: res.data.total_matched.toString(),
          reference_number: prev.reference_number ? prev.reference_number : autoRef,
        }));
        toast.success(`Found ${cur()} ${res.data.total_matched.toLocaleString()} from ${res.data.match_count} bank transactions`);
      } else if (res.data.nearby_matches_hint) {
        // iter423 — Auto-switch the period when matches exist outside
        // the picked window (banks export DD/MM/YYYY; users default to
        // "this month" — they almost never align). One more click of
        // Load from Bank now finds the deposits.
        const h = res.data.nearby_matches_hint;
        setPaymentForm(prev => ({ ...prev, period_start: h.earliest, period_end: h.latest }));
        toast.info(`Found ${h.count} matches between ${h.earliest} and ${h.latest} (${cur()} ${h.total.toLocaleString()}). Period auto-switched — click Load from Bank again.`, { duration: 12000 });
      } else {
        toast.info('No matching bank transactions found for this platform and period');
      }
    } catch { toast.error('Failed to search bank statements'); }
    finally { setBankMatching(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setCalculatedPayment(null); setBankMatches(null); } }}>
      <DialogContent className="sm:max-w-2xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Payment' : 'Record Platform Payment'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Platform *</Label>
            <Select value={paymentForm.platform_id} onValueChange={(val) => { setPaymentForm({ ...paymentForm, platform_id: val }); setCalculatedPayment(null); setBankMatches(null); }} required>
              <SelectTrigger><SelectValue placeholder="Select platform..." /></SelectTrigger>
              <SelectContent>
                {platforms.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name} ({p.commission_rate || 0}%)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Payment Date</Label>
              <Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} />
            </div>
            <div>
              <Label>Period Start *</Label>
              <Input type="date" value={paymentForm.period_start} onChange={(e) => { setPaymentForm({ ...paymentForm, period_start: e.target.value }); setCalculatedPayment(null); setBankMatches(null); }} required />
            </div>
            <div>
              <Label>Period End *</Label>
              <Input type="date" value={paymentForm.period_end} onChange={(e) => { setPaymentForm({ ...paymentForm, period_end: e.target.value }); setCalculatedPayment(null); setBankMatches(null); }} required />
            </div>
          </div>

          <div className="flex justify-center gap-2">
            <Button type="button" variant="outline" onClick={onCalculate}
              disabled={calculating || !paymentForm.platform_id || !paymentForm.period_start || !paymentForm.period_end}
              className="gap-2">
              <Calculator size={16} />
              {calculating ? 'Calculating...' : 'Calculate from Sales'}
            </Button>
            <Button type="button" variant="outline" onClick={loadFromBank}
              disabled={bankMatching || !paymentForm.platform_id || !paymentForm.period_start || !paymentForm.period_end}
              className="gap-2" data-testid="load-from-bank-btn">
              <Banknote size={16} />
              {bankMatching ? 'Searching...' : 'Load from Bank'}
            </Button>
          </div>

          {calculatedPayment && calculatedPayment.branch_breakdown?.length > 0 && (
            <div className="p-3 bg-info/10 rounded-lg border border-info/30">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Building2 size={14} /> Branch Distribution ({calculatedPayment.sales_count} sales)
              </h4>
              <div className="space-y-1.5">
                {calculatedPayment.branch_breakdown.map((branch, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm p-2 bg-white rounded">
                    <span className="font-medium">{branch.branch_name}</span>
                    <div className="text-right text-xs">
                      <span className="text-muted-foreground">Sales: {cur()} {branch.sales_amount.toLocaleString()}</span>
                      <span className="text-success font-medium ml-3">Receives: {cur()} {branch.expected_amount.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {bankMatches && bankMatches.matches?.length > 0 && (
            <div className="p-3 bg-success/10 rounded-lg border border-success/30">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Banknote size={14} /> Bank Transactions Found ({bankMatches.match_count})
                {bankMatches.owns_account_numbers && (
                  <span className="ml-auto text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Account-linked</span>
                )}
              </h4>
              <div className="space-y-1">
                {bankMatches.matches.map((m, idx) => (
                  <div key={idx} className="flex justify-between text-xs p-1.5 bg-white rounded">
                    <span className="text-muted-foreground">{m.date} — {m.description?.slice(0, 40)}</span>
                    <div className="flex items-center gap-2">
                      {m.match_via && (
                        <span className={`text-[9px] uppercase tracking-wider px-1 py-0.5 rounded ${
                          m.match_via === 'linked' ? 'bg-blue-100 text-blue-700'
                            : m.match_via === 'account' ? 'bg-emerald-100 text-emerald-700'
                              : m.match_via === 'fuzzy' ? 'bg-amber-100 text-amber-700'
                                : 'bg-slate-100 text-slate-600'
                        }`} title={`Matched via ${m.match_via}${m.confidence ? ` · ${m.confidence}% confidence` : ''}`}>
                          {m.match_via}{m.confidence ? ` ${m.confidence}%` : ''}
                        </span>
                      )}
                      <span className="font-bold text-success">{cur()} {m.amount.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between font-bold text-sm pt-2 border-t mt-2">
                  <span>Total from Bank</span>
                  <span className="text-success">{cur()} {bankMatches.total_matched.toLocaleString()}</span>
                </div>
                {/* Variance alert — fires when the entered amount_received
                    materially differs (>0.5 SR) from what the bank actually
                    deposited. Forces the user to reconcile or accept the
                    discrepancy before saving. */}
                {(() => {
                  const entered = parseFloat(paymentForm.amount_received || 0) || 0;
                  const bank = bankMatches.total_matched || 0;
                  const diff = Math.round((entered - bank) * 100) / 100;
                  if (entered <= 0 || Math.abs(diff) <= 0.5) return null;
                  return (
                    <div className="mt-2 p-2 rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-xs flex items-start gap-2" data-testid="bank-variance-alert">
                      <AlertTriangle size={13} className="mt-0.5 text-rose-600 dark:text-rose-300 shrink-0" />
                      <div className="flex-1">
                        <div className="font-bold text-rose-800 dark:text-rose-200">
                          Bank vs entered amount differs by {cur()} {Math.abs(diff).toLocaleString()}
                        </div>
                        <div className="text-rose-700 dark:text-rose-300 mt-0.5">
                          {diff > 0
                            ? `You entered ${cur()} ${Math.abs(diff).toLocaleString()} more than the bank shows — fees/commission deducted in transit?`
                            : `Bank received ${cur()} ${Math.abs(diff).toLocaleString()} more than entered — adjust the amount or expect a follow-up deposit.`}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Received-in-branch picker — required for inter-branch due tracking */}
          {branches && branches.length > 0 && (
            <div>
              <Label>Received in Branch (Bank Account)
                <span className="text-xs text-muted-foreground ml-2">— which branch's bank received the deposit?</span>
              </Label>
              <Select
                value={paymentForm.received_branch_id || ''}
                onValueChange={(val) => setPaymentForm({ ...paymentForm, received_branch_id: val })}
              >
                <SelectTrigger data-testid="received-branch-picker">
                  <SelectValue placeholder="Select the receiving branch..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Inter-branch dues preview — only shown when both received_branch
              and branch_breakdown are known and at least one OTHER branch
              earned a share. Lets the user see "Branch A will owe LV SR 225"
              before they hit Record Payment. */}
          {interBranchDues.length > 0 && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-700/50" data-testid="inter-branch-dues-preview">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2 text-amber-900 dark:text-amber-200">
                <AlertTriangle size={14} /> Inter-Branch Dues
                <span className="text-xs font-normal text-amber-700 dark:text-amber-300 ml-auto">
                  Will be auto-recorded on save
                </span>
              </h4>
              <p className="text-xs text-amber-800 dark:text-amber-300 mb-2">
                The receiving branch holds cash that belongs to other branches:
              </p>
              <div className="space-y-1">
                {interBranchDues.map((d, idx) => (
                  <div key={idx} className="flex justify-between text-xs p-1.5 bg-white dark:bg-stone-800 rounded">
                    <span className="font-medium">→ {d.branch_name}</span>
                    <span className="font-bold text-amber-700 dark:text-amber-300">{cur()} {Number(d.amount).toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between text-xs font-bold pt-1.5 border-t mt-1.5 border-amber-200 dark:border-amber-700/50">
                  <span>Total inter-branch due</span>
                  <span>{cur()} {totalDuePreview.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Total Sales {selectedPlatform && <span className="text-xs text-muted-foreground">({agreedRate}% agreed)</span>}</Label>
              <Input type="number" step="0.01" value={paymentForm.total_sales} onChange={(e) => onRecalcFromTotalSales(e.target.value)}
                placeholder="0.00" data-testid="payment-total-sales"
                className={calculatedPayment ? 'bg-success/10 border-success/30' : ''} />
            </div>
            <div>
              <Label>Commission Deducted</Label>
              <Input type="number" step="0.01" value={paymentForm.commission_paid}
                onChange={(e) => {
                  const commission = parseFloat(e.target.value) || 0;
                  const total = parseFloat(paymentForm.total_sales) || 0;
                  setPaymentForm(prev => ({ ...prev, commission_paid: e.target.value, amount_received: (total - commission) > 0 ? (total - commission).toFixed(2) : '' }));
                }}
                placeholder="0.00" data-testid="payment-commission" />
            </div>
            <div>
              <Label>Amount Received *</Label>
              <Input type="number" step="0.01" value={paymentForm.amount_received}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, amount_received: e.target.value }))}
                placeholder="Enter actual amount received" data-testid="payment-amount-received" required
                className={bankMatches?.total_matched ? 'bg-success/10 border-success/30' : ''} />
            </div>
          </div>

          <DeductionFields paymentForm={paymentForm} setPaymentForm={setPaymentForm} />
          {/* Variance Analysis */}
          {hasVariance && (
            <div className={`p-3 rounded-lg border ${rateVariance > 0 ? 'bg-error/10 border-error/30' : 'bg-success/10 border-success/30'}`}>
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                {rateVariance > 0 ? <AlertTriangle size={14} className="text-error" /> : <CheckCircle size={14} className="text-success" />}
                Commission Variance Analysis
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Agreed rate:</span><span className="font-medium">{agreedRate}%</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Expected commission:</span><span>{cur()} {expectedCommission.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Expected to receive:</span><span>{cur()} {expectedReceived.toLocaleString()}</span></div>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Actual rate:</span><span className={`font-bold ${rateVariance > 0 ? 'text-error' : 'text-success'}`}>{actualRate}%</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Actual commission:</span><span>{cur()} {actualCommission.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Actually received:</span><span>{cur()} {amountReceived.toLocaleString()}</span></div>
                </div>
              </div>
              <div className={`mt-2 pt-2 border-t text-sm font-bold flex justify-between ${rateVariance > 0 ? 'border-error/30 text-error' : 'border-success/30 text-success'}`}>
                <span>{rateVariance > 0 ? 'Platform overcharged by:' : 'Platform undercharged by:'}</span>
                <span>{cur()} {Math.abs(amountVariance).toLocaleString()} ({Math.abs(rateVariance)}% {rateVariance > 0 ? 'extra' : 'less'})</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentForm.payment_method} onValueChange={(val) => setPaymentForm({ ...paymentForm, payment_method: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="online_transfer">Online Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference #</Label>
              <Input value={paymentForm.reference_number} onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })} placeholder="Transfer ref..." />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-success hover:bg-emerald-700" data-testid="submit-payment-btn">{isEditing ? 'Update Payment' : 'Record Payment'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeductionFields({ paymentForm, setPaymentForm }) {
  const d = paymentForm.deductions || {};
  const setD = (k) => (e) => setPaymentForm((prev) => ({ ...prev, deductions: { ...(prev.deductions || {}), [k]: e.target.value } }));
  return (
    <div className="grid grid-cols-5 gap-2 rounded-lg border border-dashed p-2" data-testid="payment-deduction-fields">
      <div>
        <Label className="text-xs">VAT on commission</Label>
        <Input type="number" step="0.01" value={paymentForm.commission_vat || ''} onChange={(e) => setPaymentForm((prev) => ({ ...prev, commission_vat: e.target.value }))} placeholder="0.00" data-testid="payment-commission-vat" />
      </div>
      {[['refunds', 'Refunds'], ['penalties', 'Penalties'], ['adjustments', 'Adjustments'], ['other', 'Other charges']].map(([k, l]) => (
        <div key={k}>
          <Label className="text-xs">{l}</Label>
          <Input type="number" step="0.01" value={d[k] || ''} onChange={setD(k)} placeholder="0.00" data-testid={`payment-deduction-${k}`} />
        </div>
      ))}
      <p className="col-span-5 text-[11px] text-muted-foreground">From the platform statement. Shown separately in Reconciliation; nothing is written off or expensed automatically.</p>
      <div className="col-span-2">
        <Label className="text-xs">Commission tax-invoice no.</Label>
        <Input value={paymentForm.commission_vat_invoice_ref || ''} onChange={(e) => setPaymentForm((prev) => ({ ...prev, commission_vat_invoice_ref: e.target.value }))} placeholder="Needed to book VAT as Input VAT" data-testid="payment-commission-vat-ref" />
      </div>
    </div>
  );
}
